import { useMemo, useState, useEffect } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Table } from '../../components/ui/table';
import { StudyData } from '@/types/types';
import { createExpandableColumn, baseColumns } from './table/tableColumns';
import { TableToolbar } from './table/tableToolbar';
import { DataTableHeader } from './table/tableHeader';
import { TablePagination } from '../table-common/tablePagination';
import { DataTableBody } from './table/tableBody';
import { useExpandableRows } from './hooks/useExpandableRows';
import { useTableState } from './hooks/useTableState';
import { useTableData } from './hooks/useTableData';

interface DataTableProps {
  data: StudyData[];
}

const COLUMN_VISIBILITY_STORAGE_KEY = 'stratagen-dashboard-column-visibility';

export function DataTable({ data: initialData }: DataTableProps) {
  const data = useTableData(initialData);
  const {
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
  } = useTableState();

  const {
    expandedRows,
    loadingRows,
    errorRows,
    seriesData,
    isExpanded,
    isLoading,
    toggleRow,
  } = useExpandableRows();

  // Load saved column visibility from localStorage or use defaults
  const initialColumnVisibility = useMemo(() => {
    // Try to load from localStorage
    const savedVisibility = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility);
      } catch (error) {
        console.error('Error parsing saved column visibility:', error);
      }
    }
    
    // Fallback to defaults based on meta.isDefaultVisible
    return Object.fromEntries(
      baseColumns.map((col) => [
        col.id ?? ('accessorKey' in col ? col.accessorKey : undefined),
        col.meta && 'isDefaultVisible' in col.meta ? col.meta.isDefaultVisible === true : false,
      ]).filter(([key]) => key !== undefined)
    );
  }, []);

  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const columns = useMemo(() => {
    const expandableColumn = createExpandableColumn({
      isExpanded,
      isLoading,
      toggleRow,
    });

    return [expandableColumn, ...baseColumns];
  }, [isExpanded, isLoading, toggleRow]);

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      columnFilters,
      sorting,
      columnVisibility,
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <TableToolbar table={table} />
      <div
        className="max-h-[calc(100vh-220px)] overflow-auto rounded-[10px] border border-border bg-background mr-4 ml-4"
      >
        <Table>
          <DataTableHeader table={table} />
          <DataTableBody
            table={table}
            expandedRows={expandedRows}
            loadingRows={loadingRows}
            errorRows={errorRows}
            seriesData={seriesData}
          />
        </Table>
      </div>
      <TablePagination table={table} />
    </>
  );
}